/**
 * 设计树节点种类常量，后续几何体、基准与材质节点会共用同一投影结构。
 */
export const ProjectModelDesignTreeNodeKinds = {
  /**
   * 文件夹节点，用于分组表达 SolidWorks 特征树中的逻辑目录。
   */
  folder: 'folder',
  /**
   * 装配体模型节点，对应项目模型中的 assembly 节点。
   */
  assemblyNode: 'assembly-node',
  /**
   * 零件模型节点，对应项目模型中的 part 节点。
   */
  partNode: 'part-node',
  /**
   * 实体节点，用于后续表达零件内的可选择实体。
   */
  body: 'body',
  /**
   * 材质节点，用于后续表达模型材质信息。
   */
  material: 'material',
  /**
   * 基准面节点，用于后续表达由项目模型派生的基准面。
   */
  datumPlane: 'datum-plane',
  /**
   * 原点节点，用于后续表达模型局部坐标原点。
   */
  origin: 'origin',
} as const

/**
 * 设计树节点种类，保持与 SolidWorks 特征树语义相近的可扩展分类。
 */
export type ProjectModelDesignTreeNodeKind =
  typeof ProjectModelDesignTreeNodeKinds[keyof typeof ProjectModelDesignTreeNodeKinds]

/**
 * 项目模型设计树节点。
 */
export interface ProjectModelDesignTreeNode {
  /**
   * 设计树内部稳定 key，用于 UI 展开状态、选择状态和增量更新定位。
   */
  readonly key: string
  /**
   * 设计树展示名称，首版从模型节点 key 的最后一段派生。
   */
  readonly label: string
  /**
   * 当前设计树节点的业务种类。
   */
  readonly kind: ProjectModelDesignTreeNodeKind
  /**
   * 对应的项目模型节点 key，保留给后续选择、高亮和局部替换映射。
   */
  readonly nodeKey?: string
  /**
   * 对应的场景对象 ID，后续几何解析完成后由场景绑定阶段填充。
   */
  readonly objectId?: string
  /**
   * 对应的场景对象 ID 列表。
   *
   * 一个项目模型节点可能由多个 mesh/body 共同表达；节点级 objectId 只保留第一个代表对象，
   * 选择、高亮或后续实体层级可以使用该列表覆盖完整几何范围。
   */
  readonly objectIds?: readonly string[]
  /**
   * 对应的派生基准面 key，后续基准显示与选择阶段填充。
   */
  readonly derivedDatumPlaneKey?: string
  /**
   * 当前节点的子节点，保持与项目模型资产树一致的层级顺序。
   */
  readonly children: readonly ProjectModelDesignTreeNode[]
}

/**
 * 项目模型设计树投影。
 */
export interface ProjectModelDesignTreeProjection {
  /**
   * 设计树根节点列表，首版项目模型加载只产生一个根节点。
   */
  readonly roots: readonly ProjectModelDesignTreeNode[]
}

/**
 * 项目模型节点与场景对象的绑定关系。
 */
export interface ProjectModelDesignTreeSceneBinding {
  /**
   * 对应的项目模型节点 key。
   */
  readonly nodeKey: string
  /**
   * 设计树选择和高亮链路使用的场景对象 ID。
   */
  readonly objectId: string
}
